import { BookingOrigin } from '@utriper/core';
import { fetch, Method } from 'core/api/fetch';

const BookingRoutes = {
    reserve: '/api/v2.0/booking',
    booking: '/api/v1.1.0/experiencer/booking/confirmation',
    notifyPaymentLink: '/api/v1.1.1/payment/notification_payment_link',
    cancelBooking: (paymentId) => `/api/v1.1.0/experiencer/booking/confirmation/${paymentId}`,
    cancelPaymentLink: '/api/v1.1.1/payment/cancel_payment_link',
};

const getDurationOverride = (overrideData) => {
    const durationData = overrideData?.duration || null;

    if (!durationData) {
        return 0;
    }

    const days = parseInt(durationData?.days) || 0;
    const hours = parseInt(durationData?.hours) || 0;
    const minutes = parseInt(durationData?.minutes) || 0;

    return days * 24 * 60 + hours * 60 + minutes;
};

const parseUnits = (units = [], price = 0, overrideAvailability) => {
    const totalQuantity = units?.reduce((acc, item) => acc + Number(item?.quantity || 0), 0);
    const dividedPrice = overrideAvailability && price ? price / totalQuantity : null;

    return units
        ?.map((unit) => ({
            unitId: unit?.id,
            quantity: unit?.quantity,
            price: dividedPrice,
        }))
        ?.filter((unit) => unit?.quantity > 0);
};

const parseBookingItem = (bookingItem, overrideAvailability) => {
    const hasUnits = Array.isArray(bookingItem?.units) && bookingItem?.units?.length > 0;

    const bookingItemParsed = {
        bundleId: bookingItem?.bundleId ?? null,
        experienceId: bookingItem?.experienceId,
        experienceDetailId: bookingItem?.experienceDetailId,
        availabilityId: bookingItem?.selectedSlot?.id ?? null,
        units: hasUnits ? parseUnits(bookingItem?.units, bookingItem?.overrideData?.price, overrideAvailability) : [],
        resourcesSelected: bookingItem?.resourcesSelected ?? [],
    };

    if (overrideAvailability) {
        const { overrideData } = bookingItem;
        Object.assign(bookingItemParsed, {
            price: overrideData?.price,
            currency: overrideData?.currency,
            dateUTC: overrideData?.date ? overrideData?.date?.toISOString() : null,
            timezone: overrideData?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
            duration: getDurationOverride(overrideData),
            payNow: overrideData?.payNow || null,
        });
    }

    return bookingItemParsed;
};

export const BookingServiceV2 = {
    reserve: async ({ items = [], triper, contractId, paymentPlatformId, isPendingTour, overrideAvailability, hostAgent, additional }) => {
        try {
            const itemsParsed = items?.map((item) => parseBookingItem(item, overrideAvailability));

            const { data } = await fetch(BookingRoutes.reserve, {
                method: Method.POST,
                authenticated: true,
                data: {
                    originId: BookingOrigin.IDS.manual,
                    items: itemsParsed,
                    triper,
                    contractId,
                    paymentPlatformId,
                    isPendingTour,
                    overrideAvailability,
                    hostAgent,
                    additional,
                },
            });

            return data;
        } catch (error) {
            throw error;
        }
    },

    booking: async ({ paymentId, paymentUID, paymentPlatformId, sessionId, termsAccepted }) => {
        try {
            const { data } = await fetch(BookingRoutes.booking, {
                method: Method.POST,
                authenticated: true,
                data: {
                    paymentId,
                    paymentUID,
                    paymentPlatformId,
                    sessionId,
                    termsAccepted,
                    status: 'approved', // TEMPORAL
                    statusId: 3, // TEMPORAL
                },
            });

            return data;
        } catch (error) {
            throw error;
        }
    },

    notifyPaymentLink: async ({ email, mobilePhone, expiryDate, bookingId }) => {
        try {
            const { data } = await fetch(BookingRoutes.notifyPaymentLink, {
                method: Method.POST,
                authenticated: true,
                data: {
                    email,
                    mobilePhone,
                    expiryDate,
                    bookingId,
                },
            });

            return data;
        } catch (error) {
            throw error;
        }
    },

    cancelBooking: async (paymentId) => {
        try {
            const { data } = await fetch(BookingRoutes.cancelBooking(paymentId), {
                method: Method.DELETE,
                authenticated: true,
            });

            return data;
        } catch (error) {
            throw error;
        }
    },

    cancelPaymentLink: async (paymentLink) => {
        try {
            const { data } = await fetch(BookingRoutes.cancelPaymentLink, {
                method: Method.PATCH,
                authenticated: true,
                data: {
                    paymentLink,
                    cancelBooking: true,
                },
            });

            return data;
        } catch (error) {
            throw error;
        }
    },
};
